<template>
  <div class="sp-finder-result-details" :class="classModifiers" part="base">
    <div class="sp-finder-result-details__call-slot" part="call-slot">
      <sp-button
        v-if="phoneNumber"
        tag="a"
        color="success"
        class="sp-finder-result-details__call"
        :block="isSmallLayout"
        block-sm
        :density="callButtonDensity"
        :href="`tel:${phoneNumber}`"
        part="call"
      >
        {{ callLabel }}
      </sp-button>
    </div>

    <div class="sp-finder-result-details__grid" part="grid">
      <section v-if="hasContact" :class="sectionClass('contact')" part="section contact">
        <sp-text tag="h3" type="body-bold" class="sp-finder-result-details__heading">{{ t("contact") }}</sp-text>

        <sp-tile
          v-if="phoneNumber"
          class="sp-finder-result-details__contact"
          icon="phone-filled"
          layout="row"
          size="small"
          type="body-bold"
          :href="`tel:${phoneNumber}`"
          :label="phoneNumber"
        ></sp-tile>

        <sp-tile
          v-if="faxNumber"
          class="sp-finder-result-details__contact"
          icon="fax-filled"
          layout="row"
          size="small"
          type="body-bold"
          :href="`tel:${faxNumber}`"
          :label="faxNumber"
        ></sp-tile>
      </section>

      <section v-if="plansAcceptedLabel" :class="sectionClass('plans')" part="section plans">
        <sp-text tag="h3" type="body-bold" class="sp-finder-result-details__heading">{{
          t("plans_accepted_heading")
        }}</sp-text>
        <sp-provider-insurance-plans-modal
          v-if="props.insurancePlansUrl"
          class="sp-finder-result-details__plans-modal"
          :accepted-plans-count="props.plansAcceptedCount"
          :url="props.insurancePlansUrl"
          :trigger-label="plansAcceptedLabel"
          trigger-variant="appearance-link"
        ></sp-provider-insurance-plans-modal>
        <sp-text v-else tag="p" type="body">
          {{ plansAcceptedLabel }}
        </sp-text>
      </section>

      <!-- detail-a: provider gender / location web contact -->
      <section v-if="showGender" :class="sectionClass('gender')" part="section gender">
        <sp-text tag="h3" type="body-bold" class="sp-finder-result-details__heading">{{ t("gender") }}</sp-text>
        <sp-text tag="p" type="body">{{ gender }}</sp-text>
      </section>

      <!-- Location web contact; rendered only once a website or email is present. -->
      <section v-if="showWebContact" :class="sectionClass('web-contact')" part="section web-contact">
        <sp-text tag="h3" type="body-bold" class="sp-finder-result-details__heading">{{ t("web_contact") }}</sp-text>
        <sp-text v-if="website" tag="p" type="body">{{ t("website") }}: {{ website }}</sp-text>
        <sp-text v-if="email" tag="p" type="body">{{ t("email") }}: {{ email }}</sp-text>
      </section>

      <!-- detail-c: provider languages -->
      <section v-if="showLanguages" :class="sectionClass('languages')" part="section languages">
        <sp-text tag="h3" type="body-bold" class="sp-finder-result-details__heading">{{
          t("languages_spoken")
        }}</sp-text>
        <sp-text tag="p" type="body">{{ languages.join(", ") }}</sp-text>
      </section>

      <!-- detail-b: provider certifications / location services -->
      <section v-if="showCertifications" :class="sectionClass('certifications')" part="section certifications">
        <sp-text tag="h3" type="body-bold" class="sp-finder-result-details__heading">{{
          t("board_certification")
        }}</sp-text>
        <ul v-if="certifications.length > 1" class="sp-finder-result-details__list">
          <li v-for="certification in certifications" :key="certification" class="sp-finder-result-details__list-item">
            <sp-text tag="span" type="body">{{ certification }}</sp-text>
          </li>
        </ul>
        <sp-text v-else tag="p" type="body">{{ certifications[0] }}</sp-text>
      </section>

      <section v-if="showServices" :class="sectionClass('services')" part="section services">
        <sp-text tag="h3" type="body-bold" class="sp-finder-result-details__heading">{{
          t("location_services")
        }}</sp-text>
        <ul v-if="services.length > 1" class="sp-finder-result-details__list">
          <li v-for="service in services" :key="service" class="sp-finder-result-details__list-item">
            <sp-text tag="span" type="body">{{ service }}</sp-text>
          </li>
        </ul>
        <sp-text v-else tag="p" type="body">{{ services[0] }}</sp-text>
      </section>

      <!-- detail-d: provider education / location disclaimer -->
      <section v-if="showEducation" :class="sectionClass('education')" part="section education">
        <sp-text tag="h3" type="body-bold" class="sp-finder-result-details__heading">{{ t("education") }}</sp-text>
        <ul v-if="educations.length > 1" class="sp-finder-result-details__list">
          <li
            v-for="education in educations"
            :key="educationKey(education)"
            class="sp-finder-result-details__list-item"
          >
            <sp-text tag="span" type="body">{{ educationText(education) }}</sp-text>
          </li>
        </ul>
        <sp-text v-else tag="p" type="body">{{ educationText(educations[0]) }}</sp-text>
      </section>

      <section v-if="showDisclaimer" :class="sectionClass('disclaimer')" part="section disclaimer">
        <sp-text tag="h3" type="body-bold" class="sp-finder-result-details__heading">{{ disclaimerHeading }}</sp-text>
        <sp-text tag="p" type="body">{{ disclaimer }}</sp-text>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { i18n } from "#ce/components/SpFinder/i18n";

// Direct instance access (see SpFinderResult.ce.vue) — useI18n() can't resolve
// the plugin across the custom-element app boundary.
function t(...args) {
  return i18n.global.t(...args);
}

const props = defineProps({
  layout: {
    type: String,
    default: "lg",
    validator: (value) => ["sm", "md", "lg", "xl"].includes(value),
  },
  // Drives which set of sections renders (provider vs location).
  resultType: {
    type: String,
    default: "provider",
    validator: (value) => ["provider", "location"].includes(value),
  },
  phoneNumber: {
    type: String,
    default: "",
  },
  faxNumber: {
    type: String,
    default: "",
  },
  gender: {
    type: String,
    default: "",
  },
  languages: {
    type: Array,
    default: () => [],
  },
  certifications: {
    type: Array,
    default: () => [],
  },
  educations: {
    type: Array,
    default: () => [],
  },
  plansAcceptedCount: {
    type: Number,
    default: 0,
  },
  insurancePlansUrl: {
    type: String,
    default: undefined,
  },
  // Location web-contact. Not serialized yet; the section renders once a website or email is present.
  website: {
    type: String,
    default: "",
  },
  email: {
    type: String,
    default: "",
  },
  // Location services list. Not serialized yet; the section stays hidden until populated.
  services: {
    type: Array,
    default: () => [],
  },
  // Free-text regulatory disclaimer (publishing-domain supplied); rendered under
  // the disclaimerHeading on location results only.
  disclaimer: {
    type: String,
    default: "",
  },
  // Heading for the disclaimer section; configurable per publishing domain.
  disclaimerHeading: {
    type: String,
    default: "Important",
  },
});

const isProvider = computed(() => props.resultType === "provider");
const isLocation = computed(() => props.resultType === "location");

const phoneNumber = computed(() => props.phoneNumber);
const callLabel = computed(() => t("call", { phoneNumber: phoneNumber.value }));
const faxNumber = computed(() => props.faxNumber);
const gender = computed(() => props.gender);

const languages = computed(() => names(props.languages));
const certifications = computed(() => names(props.certifications));

// Provider-only sections (composite conditions live here; templates avoid && / ||).
const showGender = computed(() => isProvider.value && Boolean(props.gender));
const showLanguages = computed(() => isProvider.value && languages.value.length > 0);
const showCertifications = computed(() => isProvider.value && certifications.value.length > 0);
const showEducation = computed(() => isProvider.value && props.educations.length > 0);
// Location-only sections.
const showWebContact = computed(() => isLocation.value && Boolean(props.website || props.email));
const showServices = computed(() => isLocation.value && props.services.length > 0);
const showDisclaimer = computed(() => isLocation.value && Boolean(props.disclaimer));

const hasContact = computed(() => Boolean(props.phoneNumber || props.faxNumber));
const isSmallLayout = computed(() => props.layout === "sm");
const callButtonDensity = computed(() => (isSmallLayout.value ? "compact" : "default"));
const plansAcceptedLabel = computed(() => {
  if (!props.plansAcceptedCount) {
    return "";
  }

  return t("plans_accepted_count", { count: props.plansAcceptedCount }, props.plansAcceptedCount);
});

const classModifiers = computed(() => [`--layout-${props.layout}`]);

function names(items) {
  return items.map((item) => item?.name).filter(Boolean);
}

function sectionClass(name) {
  return ["sp-finder-result-details__section", `sp-finder-result-details__section--${name}`];
}

function educationText(education) {
  return [education.year && `${education.year}:`, education.school, education.title].filter(Boolean).join(" ");
}

function educationKey(education) {
  return education.id || educationText(education);
}
</script>

<style lang="scss">
:host {
  display: block;
}
</style>

<style scoped lang="scss">
.sp-finder-result-details {
  /* Layout */
  --columns-sm: var(--sp-comp-finder-result-directory-details-columns-sm, minmax(0, 1fr));
  --columns-md: var(--sp-comp-finder-result-directory-details-columns-md, repeat(3, minmax(0, 1fr)));
  --columns-lg: var(--sp-comp-finder-result-directory-details-columns-lg, repeat(2, minmax(0, 1fr)));
  --columns-xl: var(--sp-comp-finder-result-directory-details-columns-xl, repeat(3, minmax(0, 1fr)));

  /* Alignment */
  --grid-justify-content: var(--sp-comp-finder-result-directory-details-grid-justify-content, start);
  --grid-justify-content-lg: var(--sp-comp-finder-result-directory-details-grid-justify-content-lg, end);
  --grid-justify-content-md: var(--sp-comp-finder-result-directory-details-grid-justify-content-md, start);
  --grid-justify-content-sm: var(--sp-comp-finder-result-directory-details-grid-justify-content-sm, stretch);

  /* Spacing */
  --details-column-gap: var(--sp-comp-finder-result-directory-details-column-gap, var(--sp-ref-scale-16));
  --details-row-gap: var(--sp-comp-finder-result-directory-details-row-gap, var(--sp-ref-scale-24));
  --content-gap-lg: var(
    --sp-comp-finder-result-directory-details-content-gap-lg,
    var(--sp-comp-finder-result-directory-details-content-gap, var(--sp-ref-scale-36))
  );
  --content-gap-md: var(
    --sp-comp-finder-result-directory-details-content-gap-md,
    var(--sp-comp-finder-result-directory-details-content-gap, var(--sp-ref-scale-36))
  );
  --content-gap-sm: var(
    --sp-comp-finder-result-directory-details-content-gap-sm,
    var(--sp-comp-finder-result-directory-details-content-gap, var(--sp-ref-scale-36))
  );
  --section-gap: var(--sp-comp-finder-result-directory-details-section-gap, var(--sp-ref-scale-4));

  /* Color */
  --heading-color: var(--sp-comp-finder-result-directory-details-heading-color, var(--sp-sys-color-on-surface));
  --highlight-text-color: var(
    --sp-comp-finder-result-directory-details-highlight-text-color,
    var(--sp-sys-color-primary)
  );

  --columns: var(--columns-lg);
  --content-gap: var(--content-gap-lg);
  --grid-template-areas: none;
  --area-contact: auto;
  --area-plans: auto;
  // Layout-neutral secondary slots; each breakpoint remaps them. Provider and
  // location sections share a slot (only one result type mounts at a time).
  --area-detail-a: auto;
  --area-detail-b: auto;
  --area-detail-c: auto;
  --area-detail-d: auto;
  --call-slot-display: flex;
  --call-size: 3.5rem;
  --call-margin-inline-start: auto;
  --call-order: 0;
  --grid-order: 0;

  display: flex;
  flex-direction: column;
  gap: var(--content-gap);
  width: 100%;
  color: var(--sp-sys-color-on-surface, #333);
  font-family: var(--sp-sys-font-family-normal, inherit);

  &.--layout-sm {
    --columns: var(--columns-sm);
    --call-slot-display: contents;
    --call-margin-inline-start: 0;
    --call-order: 2;
    --content-gap: var(--content-gap-sm);
    --grid-order: 1;
    --grid-justify-content: var(--grid-justify-content-sm);
  }

  &.--layout-md {
    --columns: var(--columns-md);
    --call-slot-display: contents;
    --call-margin-inline-start: 0;
    --call-order: 2;
    --content-gap: var(--content-gap-md);
    --grid-order: 1;
    --grid-justify-content: var(--grid-justify-content-md);
  }

  &.--layout-lg {
    --columns: var(--columns-lg);
  }

  &.--layout-xl {
    --columns: var(--columns-xl);
  }

  &.--layout-lg,
  &.--layout-xl {
    --content-gap: var(--content-gap-lg);
    --grid-justify-content: var(--grid-justify-content-lg);
  }

  &.--layout-md,
  &.--layout-xl {
    --grid-template-areas: "contact detail-a detail-b" "plans detail-c detail-d";
    --area-contact: contact;
    --area-plans: plans;
    --area-detail-a: detail-a;
    --area-detail-b: detail-b;
    --area-detail-c: detail-c;
    --area-detail-d: detail-d;
  }

  &__call {
    --sp-comp-button-border-radius: var(--sp-ref-border-radius-xl);
    --sp-comp-button-font-weight: var(--sp-sys-font-weight-bold);
    --sp-comp-button-padding: 0 var(--sp-ref-scale-32);
    --sp-comp-button-size: var(--call-size);
    order: var(--call-order);
    margin-inline-start: var(--call-margin-inline-start);
  }

  &__call-slot {
    order: var(--call-order);
    display: var(--call-slot-display);
    justify-content: flex-end;
    min-block-size: var(--call-size);
  }

  &__grid {
    display: grid;
    grid-template-columns: var(--columns);
    grid-template-areas: var(--grid-template-areas);
    column-gap: var(--details-column-gap);
    row-gap: var(--details-row-gap);
    order: var(--grid-order);
    justify-content: var(--grid-justify-content);
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: var(--section-gap);

    &--contact {
      grid-area: var(--area-contact);
    }
    &--plans {
      grid-area: var(--area-plans);
    }
    &--gender,
    &--web-contact {
      grid-area: var(--area-detail-a);
    }
    &--certifications,
    &--services {
      grid-area: var(--area-detail-b);
    }
    &--languages {
      grid-area: var(--area-detail-c);
    }
    &--education,
    &--disclaimer {
      grid-area: var(--area-detail-d);
    }
  }

  &__heading {
    --sp-comp-text-color: var(--heading-color);
  }

  // Multi-item education / certification / services lists, with native list markers.
  &__list {
    margin: 0;
    padding-inline-start: var(--sp-ref-scale-16);
  }

  &__list-item {
    font: var(--sp-sys-typescale-body-short);

    &:not(:last-child) {
      margin-block-end: var(--sp-ref-scale-4);
    }
  }

  &__plans-modal {
    align-self: flex-start;
    --sp-comp-button-appearance-link-text-decoration: none;
    --sp-comp-button-font-short: var(--sp-sys-typescale-body-bold-short);
    --sp-comp-provider-insurance-plans-modal-width-sm: fit-content;

    &:hover {
      --sp-comp-button-appearance-link-text-decoration: underline;
    }
  }

  &__contact {
    --sp-comp-tile-inline-size: fit-content;
    --sp-comp-tile-max-inline-size: 100%;
    --sp-comp-tile-gap: var(--sp-ref-scale-8);
    --sp-comp-tile-gap-sm: var(--sp-ref-scale-8);
    --sp-comp-tile-icon-fill: var(--highlight-text-color);
    --sp-comp-tile-label-color: var(--highlight-text-color);
    --sp-comp-tile-label-color-hover: var(--highlight-text-color);
    --sp-comp-tile-label-font: var(--sp-sys-typescale-body-bold-short);
    --sp-comp-tile-label-text-decoration: none;
    --sp-comp-tile-label-text-decoration-hover: underline;
  }
}
</style>
